import { Alert, Box, Button, Flex, HStack, Stat, Text } from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { Link as RouterLink } from 'react-router-dom'
import { useSimBaseline, usePinnedScenario } from '../../hooks/useElectionSim'
import { explainCopy, formatPct, formatSignedPp } from '../../lib/explainCopy'
import type { ContestRequest, MarginBand } from '../../types/electionSim'
import type { GeoUnitProfile, GeoUnitTypeKey } from '../../types/geoAnalytics'
import { CoverageBadge } from '../electionSim/CoverageBadge'
import ProfileCard from './ProfileCard'
import { vizSeqVar } from '../shared/viz'

interface WhatIfSnapshotCardProps {
  profile: GeoUnitProfile
}

/** District unit type → ContestRequest office (ElectionOffice enum name) + display label. */
const OFFICE_BY_UNIT: Partial<Record<GeoUnitTypeKey, { office: string; label: string }>> = {
  stateHouseDistrict: { office: 'StateHouse', label: 'State House' },
  stateSenateDistrict: { office: 'StateSenate', label: 'State Senate' },
  congressionalDistrict: { office: 'USHouse', label: 'US House' },
}

export function WhatIfSnapshotCard({ profile }: WhatIfSnapshotCardProps) {
  const officeInfo = OFFICE_BY_UNIT[profile.unitType]
  const contest: ContestRequest | null = officeInfo
    ? {
        type: 'district',
        office: officeInfo.office,
        state: profile.state,
        districtGeoUnitId: profile.id,
      }
    : null

  const { data: baseline } = useSimBaseline(contest)
  const { data: pinned } = usePinnedScenario(officeInfo ? profile.id : null)

  // County (and any non-district unit) has no single-race contest to model.
  if (!officeInfo) return null

  const refusalReason = baseline?.coverage.refusalReason
  const basis = baseline?.coverage.basis
  const modeledCount = baseline
    ? baseline.coverage.modeledFromReturns + baseline.coverage.modeledFromTopOfTicket
    : 0
  const pinnedSummary = parsePinned(pinned?.lastRunSummaryJson, pinned?.name)

  return (
    <ProfileCard
      eyebrow="What-if snapshot"
      title={`${officeInfo.label} · baseline, no levers`}
      source={
        pinnedSummary
          ? `Pinned scenario: “${pinnedSummary.name}”${pinnedSummary.ranAt ? ` (ran ${pinnedSummary.ranAt})` : ''} prints with this profile`
          : '1,000 Monte Carlo draws · recency-weighted comparable contests · no field plan applied'
      }
    >
      {refusalReason ? (
        <Box mt={3}>
          <Alert.Root status="info" borderRadius="md" fontSize="sm">
            <Alert.Indicator />
            {refusalReason}
          </Alert.Root>
          <HStack mt={3} gap={2}>
            <CoverageBadge coverage="Insufficient" />
          </HStack>
        </Box>
      ) : basis === 'topOfTicket' ? (
        <HStack mt={3} gap={3} align="center" wrap="wrap">
          <CoverageBadge coverage="TopOfTicket" />
          <Tooltip content={explainCopy.topOfTicketBasis(officeInfo.label)} showArrow>
            <Text fontSize="sm" color="gray.600" cursor="help">
              Estimated from top-of-ticket — not {officeInfo.label} returns
            </Text>
          </Tooltip>
        </HStack>
      ) : (
        <Flex align="center" gap={6} wrap="wrap" mt={3}>
          <Stat.Root size="sm" flex="0 0 auto">
            <Tooltip
              content={explainCopy.winProbability(
                1000,
                baseline?.winProbability != null ? Math.round(baseline.winProbability * 100) : 0,
                profile.leanFormatted,
              )}
              showArrow
            >
              <Stat.ValueText fontSize="2xl" cursor="help" display="inline-block">
                {baseline?.winProbability != null ? formatPct(baseline.winProbability) : '—'}
              </Stat.ValueText>
            </Tooltip>
            <Stat.Label fontSize="xs" color="gray.500">
              Baseline win probability
            </Stat.Label>
            {pinnedSummary?.winProbability != null && (
              <Stat.HelpText fontSize="xs" mb={0}>
                pinned scenario: {formatPct(pinnedSummary.winProbability)}
              </Stat.HelpText>
            )}
          </Stat.Root>

          {baseline?.marginPp && <MarginBandStrip band={baseline.marginPp} />}

          {baseline && (
            <HStack gap={2} flex="0 0 auto">
              <CoverageBadge coverage="Modeled" />
              <Text fontSize="xs" color="gray.500">
                {modeledCount}/{baseline.unitCount} precincts
              </Text>
            </HStack>
          )}
        </Flex>
      )}

      <HStack mt={4}>
        <Button size="sm" colorPalette="brand" asChild><RouterLink to={`/dashboard/analytics/scenarios?district=${profile.id}`}>Simulate this race →
                  </RouterLink></Button>
      </HStack>
    </ProfileCard>
  );
}

interface PinnedSummary {
  name: string
  winProbability: number | null
  ranAt: string | null
}

function parsePinned(json: string | null | undefined, name: string | undefined): PinnedSummary | null {
  if (!json || !name) return null
  try {
    const parsed = JSON.parse(json) as { winProbability?: number | null; ranAt?: string | null }
    return {
      name,
      winProbability: parsed.winProbability ?? null,
      ranAt: parsed.ranAt ? new Date(parsed.ranAt).toLocaleDateString() : null,
    }
  } catch {
    return { name, winProbability: null, ranAt: null }
  }
}

/**
 * Baseline margin-band strip (p10–p90 with a p50 marker), geometry copied from
 * the slice-4 mockup: 270×56 canvas, dashed "even" line, band rect, tick label
 * pair. pp values map onto a symmetric [-D, D] domain and are clamped.
 */
function MarginBandStrip({ band }: { band: MarginBand }) {
  const W = 270
  const H = 56
  const xL = 10
  const xR = 260
  const D = Math.max(5, Math.ceil(Math.max(Math.abs(band.p10), Math.abs(band.p90))) + 1)
  const scale = (pp: number) => {
    const t = (Math.max(-D, Math.min(D, pp)) + D) / (2 * D)
    return xL + t * (xR - xL)
  }
  const xEven = scale(0)
  const xLo = scale(band.p10)
  const xHi = scale(band.p90)
  const xMid = scale(band.p50)
  const bandLeft = Math.min(xLo, xHi)
  const bandW = Math.max(4, Math.abs(xHi - xLo))

  return (
    <svg
      width={W}
      height={H}
      role="img"
      aria-label={`Baseline margin band: p10 ${formatSignedPp(band.p10)} to p90 ${formatSignedPp(band.p90)}, median ${formatSignedPp(band.p50)}`}
    >
      <line x1={xL} y1={30} x2={xR} y2={30} stroke="var(--chakra-colors-muted)" strokeWidth={1} />
      <line x1={xEven} y1={12} x2={xEven} y2={46} stroke="var(--chakra-colors-muted)" strokeWidth={1} strokeDasharray="3 3" />
      <text x={xEven} y={10} fontSize={9} fill="var(--chakra-colors-muted)" textAnchor="middle">
        even
      </text>
      <rect x={bandLeft} y={24} width={bandW} height={12} rx={4} fill={vizSeqVar(200)} />
      <rect x={xMid - 2} y={20} width={4} height={20} rx={2} fill={vizSeqVar(600)} />
      <text x={xLo} y={52} fontSize={10} fill="var(--chakra-colors-ink-2)" textAnchor="middle">
        {formatSignedPp(band.p10)}
      </text>
      <text x={xMid} y={14} fontSize={10} fill="var(--chakra-colors-ink)" textAnchor="middle" fontWeight={700}>
        {formatSignedPp(band.p50)}
      </text>
      <text x={xHi} y={52} fontSize={10} fill="var(--chakra-colors-ink-2)" textAnchor="middle">
        {formatSignedPp(band.p90)}
      </text>
    </svg>
  )
}
